'use client';
// ═══════════════════════════════════════════════════════════════════════════
// « PRÉVENEZ-MOI QUAND CETTE CP BOUGE » — le seul bloc capteur des 474 pages
// ───────────────────────────────────────────────────────────────────────────
// Ces pages sont les mieux placées du site (1er sur « barème CP 200 » au
// 22/08/2026, devant Securex et la FGTB) et n'avaient qu'UN appel à l'action :
// « nous contacter ». C'est l'engagement maximal demandé d'emblée à quelqu'un
// venu vérifier un chiffre. Ici, la demande est minuscule et le service est
// réel : le robot de veille détecte déjà les indexations sectorielles.
//
// FR + NL (R8). Le composant est défini AU NIVEAU DU MODULE, jamais dans un
// return (R4) : une définition imbriquée fait perdre le focus du champ à
// chaque frappe — exactement ce qu'il ne faut pas sur un champ e-mail.
// ═══════════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useRecaptcha } from '@/app/lib/recaptcha';

const G = '#c9a961';

const T = {
  fr: {
    titre: (cp) => `Le barème de la CP ${cp} va changer. Soyez prévenu.`,
    texte:
      'Les barèmes sectoriels sont indexés en cours d’année, à des dates qui diffèrent d’une commission à l’autre. Laissez votre adresse : nous écrivons le jour où celui-ci bouge, avec le nouveau montant et sa source officielle. Rien d’autre.',
    champ: 'votre@adresse.be',
    bouton: 'Me prévenir',
    envoi: 'Envoi…',
    consentement:
      'J’accepte d’être prévenu par e-mail quand ce barème est indexé ou modifié. Je peux me désinscrire à tout moment.',
    ok: '✓ C’est noté. Vous recevrez un e-mail le jour où ce barème change.',
    erreurMail: 'Cette adresse ne semble pas valide.',
    erreurConsent: 'Merci de cocher la case : sans votre accord, nous ne pouvons rien enregistrer.',
    erreur: 'Impossible d’enregistrer pour le moment. Réessayez dans un instant.',
  },
  nl: {
    titre: (cp) => `Het barema van PC ${cp} gaat veranderen. Laat u verwittigen.`,
    texte:
      'Sectorale barema’s worden in de loop van het jaar geïndexeerd, op datums die van comité tot comité verschillen. Laat uw adres achter: wij schrijven u de dag dat dit barema wijzigt, met het nieuwe bedrag en de officiële bron. Niets anders.',
    champ: 'uw@adres.be',
    bouton: 'Verwittig mij',
    envoi: 'Bezig…',
    consentement:
      'Ik ga ermee akkoord per e-mail verwittigd te worden wanneer dit barema geïndexeerd of gewijzigd wordt. Ik kan mij op elk moment uitschrijven.',
    ok: '✓ Genoteerd. U krijgt een e-mail zodra dit barema wijzigt.',
    erreurMail: 'Dit adres lijkt niet geldig.',
    erreurConsent: 'Vink het vakje aan: zonder uw akkoord mogen wij niets bewaren.',
    erreur: 'Registreren lukt nu niet. Probeer het zo dadelijk opnieuw.',
  },
};

export default function AlerteCp({ cpKey, lang = 'fr', source }) {
  const t = T[lang] || T.fr;
  const [email, setEmail] = useState('');
  const [accord, setAccord] = useState(false);
  const [etat, setEtat] = useState('repos'); // repos | envoi | ok | erreur
  const [message, setMessage] = useState('');
  const { getToken } = useRecaptcha();

  async function envoyer(e) {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setEtat('erreur'); setMessage(t.erreurMail); return;
    }
    if (!accord) { setEtat('erreur'); setMessage(t.erreurConsent); return; }
    setEtat('envoi'); setMessage('');
    try {
      const recaptchaToken = await getToken('alerte_cp');
      const r = await fetch('/api/alertes-cp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), cp: cpKey, lang, source, consent: true, recaptchaToken }),
      });
      if (!r.ok) throw new Error('http');
      setEtat('ok'); setMessage(t.ok); setEmail('');
    } catch {
      setEtat('erreur'); setMessage(t.erreur);
    }
  }

  if (etat === 'ok') {
    return (
      <div style={{ maxWidth: 720, margin: '28px auto 0', background: '#f2f7f2', borderRadius: 10, padding: 20, borderLeft: '3px solid #4a8a4a' }}>
        <p style={{ margin: 0, fontSize: 15, color: '#2c5c2c', fontWeight: 600 }}>{message}</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '28px auto 0', background: '#fdfaf3', borderRadius: 10, padding: 20, borderLeft: `3px solid ${G}` }}>
      <h2 style={{ fontSize: 17, fontWeight: 800, color: '#1a1a18', margin: '0 0 8px' }}>{t.titre(cpKey)}</h2>
      <p style={{ fontSize: 14.5, lineHeight: 1.6, color: '#4a463c', margin: '0 0 14px' }}>{t.texte}</p>
      <form onSubmit={envoyer}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <input
            type="email"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder={t.champ}
            aria-label={t.champ}
            autoComplete="email"
            style={{ flex: '1 1 240px', minWidth: 0, padding: '11px 13px', border: '1px solid #ddd4bc', borderRadius: 8, fontSize: 15, background: '#fff' }}
          />
          <button
            type="submit"
            disabled={etat === 'envoi'}
            style={{ padding: '11px 20px', background: '#1a1a18', color: '#f5f0e4', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: etat === 'envoi' ? 'wait' : 'pointer' }}
          >
            {etat === 'envoi' ? t.envoi : t.bouton}
          </button>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 12.5, lineHeight: 1.5, color: '#6a6558', cursor: 'pointer' }}>
          <input type="checkbox" checked={accord} onChange={(ev) => setAccord(ev.target.checked)} style={{ marginTop: 2 }} />
          <span>{t.consentement}</span>
        </label>
        {etat === 'erreur' && (
          <p role="alert" style={{ margin: '10px 0 0', fontSize: 13.5, color: '#9a3b2e' }}>{message}</p>
        )}
      </form>
    </div>
  );
}
